/**
 * Focus Management for Modals
 *
 * Manages focus state when opening/closing modals
 * @module portal/modules/ui/modals/focusManager
 */

/**
 * Stores the last active element before each modal type opens
 */
export const focusState = {
  lastActiveBeforeModal: null as HTMLElement | null,
  lastActiveBeforeImagesModal: null as HTMLElement | null,
  lastActiveBeforeLotDetailsModal: null as HTMLElement | null,
  lastActiveBeforeDashboardModal: null as HTMLElement | null,
};

/**
 * Captures the currently focused element before opening a modal
 * @param modalType - The type of modal being opened
 */
export function captureFocus(modalType: keyof typeof focusState): void {
  if (document.activeElement instanceof HTMLElement) {
    focusState[modalType] = document.activeElement;
  } else {
    focusState[modalType] = null;
  }
}

/**
 * Restores focus to the previously focused element after closing a modal
 * @param modalType - The type of modal being closed
 * @param fallback - Optional fallback element to focus if no previous element exists
 */
export function restoreFocus(modalType: keyof typeof focusState, fallback?: HTMLElement): void {
  const target = focusState[modalType] || fallback;
  if (target && typeof target.focus === 'function') {
    target.focus();
  }
  focusState[modalType] = null;
}

/**
 * Clears the stored focus reference for a modal
 * @param modalType - The type of modal to clear
 */
export function clearFocus(modalType: keyof typeof focusState): void {
  focusState[modalType] = null;
}
